/**
 * Device Identification Utility for Silent Login
 * 
 * This utility manages device-specific identification for silent login functionality.
 * The device ID is stored in localStorage and persists across sessions on the same device/browser.
 * 
 * If a user clears localStorage or uses a different browser/device, a new device ID will be generated,
 * resulting in a new silent login user.
 */

/**
 * Generates a unique device identifier
 * Format: device_[timestamp]_[random]
 * @returns {string} Unique device ID
 */
export const generateDeviceId = () => {
	const timestamp = Date.now();
	const random = Math.random().toString(36).substring(2, 15);
	const randomPart2 = Math.random().toString(36).substring(2, 15);
	return `device_${timestamp}_${random}${randomPart2}`;
};

/**
 * Gets the existing device ID or creates a new one if it doesn't exist
 * @returns {string} Device ID
 */
export const getDeviceId = () => {
	const DEVICE_ID_KEY = 'akedo_device_id';
	
	try {
		// Check if device ID already exists
		let deviceId = localStorage.getItem(DEVICE_ID_KEY);
		
		if (!deviceId) {
			// Generate new device ID
			deviceId = generateDeviceId();
			localStorage.setItem(DEVICE_ID_KEY, deviceId);
			console.log('[Silent Login] New device ID created:', deviceId);
		} else {
			console.log('[Silent Login] Existing device ID found:', deviceId);
		}
		
		return deviceId;
	} catch (error) {
		console.error('[Silent Login] Error accessing localStorage:', error);
		// Fallback to session-based ID if localStorage is unavailable
		return `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
	}
};

/**
 * Clears the device ID from storage
 * Use with caution - this will cause a new silent login user to be created on next visit
 */
export const clearDeviceId = () => {
	const DEVICE_ID_KEY = 'akedo_device_id';
	try {
		localStorage.removeItem(DEVICE_ID_KEY);
		console.log('[Silent Login] Device ID cleared');
	} catch (error) {
		console.error('[Silent Login] Error clearing device ID:', error);
	}
};

/**
 * Checks if user is on a silent login session (device-based)
 * @returns {boolean} True if silent login, false if connected login
 */
export const isSilentLoginSession = () => {
	try {
		const silentLoginFlag = localStorage.getItem('akedo_is_silent_login');
		return silentLoginFlag === 'true';
	} catch (error) {
		return false;
	}
};

/**
 * Sets the silent login flag
 * @param {boolean} isSilent - Whether this is a silent login session
 */
export const setSilentLoginFlag = (isSilent) => {
	try {
		localStorage.setItem('akedo_is_silent_login', isSilent ? 'true' : 'false');
	} catch (error) {
		console.error('[Silent Login] Error setting silent login flag:', error);
	}
};

/**
 * Clears silent login flag (used when upgrading to connected account)
 */
export const clearSilentLoginFlag = () => {
	try {
		localStorage.removeItem('akedo_is_silent_login');
	} catch (error) {
		console.error('[Silent Login] Error clearing silent login flag:', error);
	}
};
